// Per-anchor role-list constants for Turbo Logistics.
//
// `LOGISTICS_COORDINATOR_ROLES` replaces today's `LOGISTICS_ROLES =
// MFG_ROLES` alias — same effective behavior (a `manufacturing`
// umbrella holder still passes), but the name signals the gate is for
// the Logistics bucket, not for the driver app (DRIVER_ROLES) or the
// third-party hauler scoped login
// (THIRD_PARTY_HAULER_SUBCOORDINATOR_ROLES).
//
// `DRIVER_ROLES` and `THIRD_PARTY_HAULER_SUBCOORDINATOR_ROLES` are
// intentionally NARROW — they do NOT include the `manufacturing`
// umbrella. The host surfaces they gate (driver app, scoped-login
// surface) don't exist yet (turbo_logistics.md §6.3 + §6.5); admitting
// every current `manufacturing` user automatically would defeat the
// point of the carved tokens when those surfaces ship.
//
// Spec: docs/design/role_taxonomy.md §4 (token list) + §5.3 (canonical
// `logistics_coordinator` vs alias `dispatcher`) + §6 (per-anchor
// module home).

/**
 * Logistics-bucket coordinator gate. Full bucket access — Routes,
 * Carriers, future Calendar / Contractors / Maps; drag-and-drop rig
 * assignment; carrier admin.
 *
 * `logistics_coordinator` is the canonical DB token; `dispatcher` is
 * a doc-side alias / familiar industry label that the role matcher
 * accepts (per role-taxonomy §5.3 — `dispatcher` is a homonym in
 * other industries, e.g. emergency-services / call-center, so the
 * canonical name is the longer unambiguous one).
 */
export const LOGISTICS_COORDINATOR_ROLES: string[] = [
  "logistics_coordinator",
  "dispatcher",
  "manufacturing",
  // Org operators (not floor sales) — retail dealers dispatch from owner/admin
  // seats until a sales-embedded logistics surface ships (pilot smoke 2026-06).
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/**
 * Driver-app surface gate. The host surface (mobile driver view with
 * offline cache, FastField actor view, customer-contact loop) is a
 * future-build slice per turbo_logistics.md §6.3 + §8.
 *
 * Intentionally narrow: a `driver`-only user sees the driver app and
 * nothing else. NO `manufacturing` umbrella — drivers are not desk
 * Mfg users, and admitting every today's `manufacturing` user would
 * dilute test coverage of the driver-only gate when the surface
 * ships. Identity-level-vs-org-scoped placement of the `driver`
 * token is a §10 follow-up; for now the role matcher treats it as
 * org-scoped (a row in `user_roles` with org_id set).
 */
export const DRIVER_ROLES: string[] = [
  "driver",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/**
 * Third-party hauler scoped sub-coordinator gate. View-only over
 * routes assigned to one `carrier_id` (per turbo_logistics.md §6.5).
 * Login surface (subdomain vs scoped link vs signed-URL) is deferred
 * to the implementation slice per role-taxonomy §10 #4; the token's
 * gate anchor lives here.
 *
 * Intentionally narrow — same rationale as `DRIVER_ROLES`: no
 * `manufacturing` umbrella, no other escape hatches except the
 * platform support tier.
 */
export const THIRD_PARTY_HAULER_SUBCOORDINATOR_ROLES: string[] = [
  "third_party_hauler_subcoordinator",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];
